import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { decisionOutcome, workflowDisplayName } from "@/components/admin_dashboard/WorkflowActivity";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Mail,
  Workflow,
  MessageSquare,
  Send,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  CalendarDays,
  Info,
  Activity,
  ShieldCheck,
  ChevronDown,
  ImageOff,
  Image as ImageIcon,
  Sparkles,
  Trash2,
  AlertTriangle,
  Network,
  BookOpen,
} from "lucide-react";
import { format, formatDistanceToNow, subDays, subMonths, isAfter } from "date-fns";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip as InfoTooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import {
  WhatsAppIcon,
  TelegramIcon,
  InstagramIcon,
  MessengerIcon,
  LineIcon,
} from "@/components/admin_dashboard/BrandIcons";
import CustomerRelationships, { useRelationshipSuggestionCount } from "@/components/admin_dashboard/CustomerRelationships";
import ContactApprovalQueue, { useContactApprovalQueue } from "@/components/admin_dashboard/ContactApprovalQueue";
import ContactNotesCard from "@/components/admin_dashboard/ContactNotesCard";
import ContactPhoto from "@/components/admin_dashboard/ContactPhoto";
import ContactSocialProfilesCard from "@/components/admin_dashboard/ContactSocialProfilesCard";
import ContactChannelsSection, { type SocialPlatform } from "@/components/admin_dashboard/ContactChannelsSection";
import { contactDisplayName, isRawIdLabel, primaryIdentity, type ContactIdentifier } from "@/lib/contactDisplay";
import {
  type PipelineStage,
  STAGE_META,
} from "@/lib/pipelineStage";
import { type ContactGroup } from "@/components/admin_dashboard/ManageGroupsDialog";
import {
  ClaimFeedbackMenu,
  InferredClaim,
} from "@/components/admin_dashboard/CustomerSalesIntelligence";
import PlanFeatureGate from "@/components/admin_dashboard/PlanFeatureGate";
import { useTerminology } from "@/contexts/TerminologyContext";
import SampleCustomerProfile from "@/components/admin_dashboard/SampleCustomerProfile";
import { isDemoSampleCustomer } from "@/lib/customerSamples";
import { buildAdminUrl } from "@/lib/adminOrigin";
import {
  ATTRIBUTE_SOURCE_ICONS,
  CUSTOMER_ATTRIBUTES,
  loadCustomAttributes,
  loadHiddenAttributes,
  type CustomerAttributeDefinition,
  type CustomerAttributeKey,
} from "@/lib/customerAttributes";
import { customerInsightsFromProjection, SENTIMENT_META } from "@/lib/customerInsights";

// Campaign history is hidden for now. Flip to true to restore it.
const SHOW_CAMPAIGN_HISTORY = false;
// Social profile research is hidden until the product can reliably support it.
const SHOW_SOCIAL_PROFILES = false;
// Legacy left-rail intelligence remains disabled while the standalone
// AI Insights section owns the generated brief.
const SHOW_LEFT_PROFILE_INTELLIGENCE = false;
const CURRENT_SYNOPSIS_SCHEMA_VERSION = 3;

// Channel → brand icon for the activity timeline. Falls back to a generic mail
// icon for email and a chat bubble for anything unrecognised.
const ChannelIcon = ({ channel }: { channel: string }) => {
  const ch = channel.toLowerCase();
  const icon =
    ch === "telegram" ? <TelegramIcon size={14} /> :
    ch === "instagram" ? <InstagramIcon size={14} /> :
    ch === "facebook_messenger" ? <MessengerIcon size={14} /> :
    ch === "line" ? <LineIcon size={14} /> :
    ch === "whatsapp" ? <WhatsAppIcon size={14} /> :
    ch === "email" ? <Mail className="h-3.5 w-3.5 text-muted-foreground" /> :
    <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />;
  return (
    <span className="inline-flex shrink-0" title={channel} aria-label={channel}>
      {icon}
    </span>
  );
};

const safeFormat = (iso: string | null | undefined, fmt: string, fallback = "—") => {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return fallback;
  return format(d, fmt);
};

const safeDistance = (iso: string | null | undefined, fallback = "—") => {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return fallback;
  return formatDistanceToNow(d, { addSuffix: true });
};

const StatBlock = ({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: React.ReactNode;
  tooltip?: string;
}) => (
  <div className="rounded-lg border bg-card px-6 py-4">
    <div className="flex items-center gap-1">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      {tooltip && (
        <InfoTooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`How ${label} is calculated`}
              className="text-muted-foreground/50 transition-colors hover:text-muted-foreground"
            >
              <Info className="h-3 w-3" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[240px] text-xs font-normal leading-relaxed">
            {tooltip}
          </TooltipContent>
        </InfoTooltip>
      )}
    </div>
    <p className="mt-1 text-base font-semibold">{value}</p>
  </div>
);

// Older deterministic synopses used a terse `status: facts` template. Keep
// stored data intact while presenting those legacy rows as natural prose.
const naturalizeSynopsis = (value: string): string => {
  const legacy = value.match(
    /^(active|vip|new|dormant|lapsed|at[- ]risk) customer:\s*(\d+) recorded bookings?,\s*\$([\d,.]+) lifetime value,\s*last active (.+?)\.?$/i,
  );
  if (!legacy) return value;
  const [, rawStatus, bookings, lifetimeValue, lastActive] = legacy;
  const status = rawStatus.toLowerCase() === "vip" ? "VIP" : rawStatus.toLowerCase().replace("-", " ");
  const article = status === "active" || status === "at risk" ? "an" : "a";
  return `This is ${article} ${status} customer with ${bookings} ${bookings === "1" ? "booking" : "bookings"} and a lifetime value of $${lifetimeValue}. They were last active ${lastActive}.`;
};

const eventIcon = (type: string) => {
  if (type.includes("Sent")) return <Send className="h-3.5 w-3.5" />;
  if (type.includes("Received")) return <MessageSquare className="h-3.5 w-3.5" />;
  return <MessageSquare className="h-3.5 w-3.5" />;
};

// Turn an event type like "MessageSent" / "booking_created" into a readable
// sentence-case label: "Message sent", "Booking created".
const humanizeEventType = (type: string) => {
  if (!type) return "Event";
  const spaced = type
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

type CustomerProfileRecord = {
  business_id: string | null;
  created_at: string;
  custom_attributes?: Record<string, unknown>;
  email: string;
  name: string;
  phone: string | null;
  short_id: string | null;
};

const CustomerProfileTemplate = () => {
  const { customer: customerTerm, customers: customerTerms } = useTerminology();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get("id");
  const linkedContactId = searchParams.get("contact") ?? searchParams.get("graph");
  const profileReference = customerId ?? linkedContactId;

  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [pendingGroupId, setPendingGroupId] = useState<string | null>(null);
  const [editingAttributeKey, setEditingAttributeKey] = useState<CustomerAttributeKey | null>(null);
  const [attributeDraft, setAttributeDraft] = useState("");
  // Escape unmounts the focused input, and the blur that can fire on the way
  // out must not save (an empty draft would clear the attribute).
  const attributeEditCancelledRef = useRef(false);
  const customerAttributeDefinitions = useMemo<CustomerAttributeDefinition[]>(() => {
    const hiddenAttributes = loadHiddenAttributes();
    return [
      ...CUSTOMER_ATTRIBUTES,
      ...loadCustomAttributes().map((attribute) => ({
        key: `custom:${attribute.id}` as const,
        label: attribute.name,
        source: attribute.source,
        width: 180,
        icon: ATTRIBUTE_SOURCE_ICONS[attribute.source],
      })),
    ].filter((attribute) => attribute.key !== "name" && attribute.key !== "channel" && !hiddenAttributes.has(attribute.key));
  }, []);
  const {
    data: customer,
    isLoading,
    isError: customerLoadError,
    refetch: refetchCustomer,
  } = useQuery<CustomerProfileRecord | null>({
    queryKey: ["customer-profile", customerId, linkedContactId],
    queryFn: async () => {
      if (customerId) {
        const { data, error } = await supabase
          .from("customers")
          .select("business_id, created_at, email, name, phone, short_id")
          .eq("customer_id", customerId)
          .maybeSingle();
        if (error) throw error;
        return data;
      }
      if (!linkedContactId) return null;
      const { data, error } = await supabase
        .from("contacts")
        .select("business_id, created_at, custom_attributes, display_name, primary_email, primary_phone")
        .eq("contact_id", linkedContactId)
        .is("tombstoned_at", null)
        .maybeSingle();
      if (error) throw error;
      return data
        ? {
            business_id: data.business_id,
            created_at: data.created_at,
            custom_attributes: (data.custom_attributes as Record<string, unknown> | null) ?? {},
            email: data.primary_email ?? "",
            // Empty, not "Unnamed": the header falls back to the contact's
            // phone or @handle first (src/lib/contactDisplay.ts).
            name: data.display_name ?? "",
            phone: data.primary_phone,
            short_id: null,
          }
        : null;
    },
    enabled: !!profileReference,
  });

  // An old link to a merged-away profile opens the profile it was merged into.
  const { data: mergedIntoContactId } = useQuery({
    queryKey: ["contact-merged-into", linkedContactId],
    enabled: !customerId && !!linkedContactId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("contacts")
        .select("merged_into")
        .eq("contact_id", linkedContactId!)
        .maybeSingle();
      return (data?.merged_into as string | null) ?? null;
    },
  });
  useEffect(() => {
    if (!mergedIntoContactId) return;
    const next = new URLSearchParams(searchParams);
    next.delete("graph");
    next.set("contact", mergedIntoContactId);
    navigate(`?${next.toString()}`, { replace: true });
  }, [mergedIntoContactId, navigate, searchParams]);

  // Resolve the Customer Graph contact_id using phone/email
  const {
    data: contactId,
    isError: contactIdError,
    refetch: refetchContactId,
  } = useQuery({
    queryKey: ["contact_id_map", customerId, linkedContactId, customer?.email, customer?.phone],
    enabled: !!customer,
    queryFn: async () => {
      if (linkedContactId) {
        const { data: linked, error: linkedError } = await supabase
          .from("contacts")
          .select("contact_id")
          .eq("contact_id", linkedContactId)
          .eq("business_id", customer!.business_id)
          .is("tombstoned_at", null)
          .maybeSingle();
        if (linkedError) throw linkedError;
        if (linked) return linked.contact_id;
      }
      const identifiers = [customer?.email, customer?.phone].filter(Boolean);
      if (identifiers.length === 0) return null;

      const { data, error } = await (supabase as any)
        .from("channel_identifiers")
        .select("contact_id")
        .eq("business_id", customer!.business_id)
        .in("identifier_value", identifiers)
        .limit(1)
        .maybeSingle();

      if (error) {
        throw error;
      }
      return data?.contact_id || null;
    }
  });

  const { data: contactCustomAttributes = customer?.custom_attributes ?? {} } = useQuery({
    queryKey: ["contact_custom_attributes", contactId],
    enabled: !!contactId && !!customer?.business_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contacts")
        .select("custom_attributes")
        .eq("business_id", customer!.business_id)
        .eq("contact_id", contactId!)
        .is("tombstoned_at", null)
        .maybeSingle();
      if (error) throw error;
      return (data?.custom_attributes as Record<string, unknown> | null) ?? {};
    },
  });

  // Every channel identity on the profile, plus owner-added social handles.
  const { data: channelData, refetch: refetchChannels } = useQuery({
    queryKey: ["contact-channels", contactId],
    enabled: !!contactId && !!customer?.business_id,
    queryFn: async () => {
      const [identifiersResult, contactResult] = await Promise.all([
        (supabase as any)
          .from("channel_identifiers")
          .select("channel_type, identifier_value, handle, first_seen_at, last_seen_at")
          .eq("business_id", customer!.business_id)
          .eq("contact_id", contactId!),
        (supabase as any)
          .from("contacts")
          .select("social_handles")
          .eq("business_id", customer!.business_id)
          .eq("contact_id", contactId!)
          .maybeSingle(),
      ]);
      if (identifiersResult.error) throw identifiersResult.error;
      if (contactResult.error) throw contactResult.error;
      return {
        identifiers: (identifiersResult.data ?? []) as ContactIdentifier[],
        socialHandles: (contactResult.data?.social_handles ?? {}) as Partial<Record<SocialPlatform, string>>,
      };
    },
  });

  const { data: contactGroups = [] } = useQuery({
    queryKey: ["contact_groups", customer?.business_id],
    enabled: !!customerId && !!customer?.business_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact_groups")
        .select("id, name, color")
        .eq("business_id", customer!.business_id)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as ContactGroup[];
    },
  });

  const { data: groupMemberships = [] } = useQuery({
    queryKey: ["contact_group_members", customer?.business_id, customerId],
    enabled: !!customer?.business_id && !!customerId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact_group_members")
        .select("group_id")
        .eq("business_id", customer!.business_id)
        .eq("customer_id", customerId!);
      if (error) throw error;
      return data ?? [];
    },
  });

  const memberGroupIds = useMemo(
    () => new Set(groupMemberships.map((membership) => membership.group_id)),
    [groupMemberships],
  );
  const memberGroups = contactGroups.filter((group) => memberGroupIds.has(group.id));

  const updateGroupMembership = async (group: ContactGroup, shouldAdd: boolean) => {
    if (!customerId || !customer?.business_id) return;
    setPendingGroupId(group.id);
    const { error } = shouldAdd
      ? await supabase.from("contact_group_members").insert({
          group_id: group.id,
          customer_id: customerId,
          business_id: customer.business_id,
        })
      : await supabase
          .from("contact_group_members")
          .delete()
          .eq("group_id", group.id)
          .eq("customer_id", customerId);
    setPendingGroupId(null);

    if (error && !(shouldAdd && error.code === "23505")) {
      toast.error(`Failed to ${shouldAdd ? "add to" : "remove from"} group: ${error.message}`);
      return;
    }
    toast.success(shouldAdd ? `Added to ${group.name}` : `Removed from ${group.name}`);
    queryClient.invalidateQueries({ queryKey: ["contact_group_members", customer.business_id] });
  };

  const {
    data: projection,
    isError: projectionError,
    refetch: refetchProjection,
  } = useQuery({
    queryKey: ["contact_projection", contactId],
    enabled: !!contactId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("contact_projections")
        .select("*")
        .eq("business_id", customer!.business_id)
        .eq("contact_id", contactId)
        .maybeSingle();
      if (error && error.code !== "42P01" && error.code !== "PGRST205") throw error;
      return data ?? null;
    },
  });

  const { data: events } = useQuery({
    queryKey: ["customer_events", contactId],
    enabled: !!contactId,
    queryFn: async (): Promise<any[]> => {
      const { data, error } = await (supabase as any)
        .from("contact_events")
        .select("*")
        .eq("business_id", customer!.business_id)
        .eq("contact_id", contactId)
        .order("occurred_at", { ascending: false })
        .limit(50);
      if (error) {
        if (error.code === "42P01" || error.code === "PGRST205") return [];
        throw error;
      }
      return data ?? [];
    },
  });

  // Workflow runs that actually executed for this customer, shown alongside
  // their events. Paused and skipped decisions are left out: nothing happened.
  const { data: workflowRuns } = useQuery({
    queryKey: ["customer_workflow_runs", contactId],
    enabled: !!contactId && !!customer?.business_id,
    queryFn: async (): Promise<any[]> => {
      const { data, error } = await (supabase as any)
        .from("orchestrator_decisions")
        .select("id, action, reason, created_at, selected_workflow_id")
        .eq("business_id", customer!.business_id)
        .eq("contact_id", contactId)
        .in("action", ["activate_workflow", "escalate_to_human"])
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) return [];
      return data ?? [];
    },
  });

  const {
    data: totalOrderValue,
    isError: totalOrderValueError,
    refetch: refetchTotalOrderValue,
  } = useQuery({
    queryKey: ["customer_order_value", customer?.email, customer?.phone],
    enabled: !!customer,
    queryFn: async () => {
      let query = supabase
        .from("bookings")
        .select(`
          status,
          listings!inner (price, business_id)
        `)
        .eq("listings.business_id", customer!.business_id);
      if (customer!.email && customer!.phone) {
        query = query.or(`customer_email.eq.${customer!.email},customer_phone.eq.${customer!.phone}`);
      } else if (customer!.email) {
        query = query.eq("customer_email", customer!.email);
      } else if (customer!.phone) {
        query = query.eq("customer_phone", customer!.phone);
      } else {
        return 0;
      }
      const { data, error } = await query;

      if (error) throw error;
      return data?.reduce((acc, b) => {
        if (String(b.status).toLowerCase() === "cancelled") return acc;
        return acc + (Number((b.listings as any)?.price) || 0);
      }, 0) || 0;
    }
  });

  const customerNameForDeletion = customer?.name || "Unnamed";
  const deleteConfirmationMatches = deleteConfirmation === customerNameForDeletion;

  const handleDeleteCustomerData = async () => {
    if (!profileReference || !customer?.business_id || isDeleting) return;
    if (!deleteConfirmationMatches) return;
    setIsDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-data-delete", {
        body: {
          business_id: customer.business_id,
          ...(customerId ? { customer_id: customerId } : {}),
          contact_id: contactId ?? undefined,
          confirmation: "DELETE",
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.success(`${customerTerm} data permanently deleted`);
      setIsDeleteDialogOpen(false);
      setDeleteConfirmation("");
      navigate("/?tab=customers");
    } catch (error: any) {
      toast.error(error?.message || `Failed to delete ${customerTerm.toLowerCase()} data`);
    } finally {
      setIsDeleting(false);
    }
  };

  const [timeRange, setTimeRange] = useState<'all' | '1m' | '1w'>('all');
  const [isSendingWinback, setIsSendingWinback] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);

  // Group CampaignSent/Responded/Converted/Ignored events by campaign_id so the
  // Campaign History card can show one row per campaign with its outcome.
  const campaignHistory = useMemo(() => {
    if (!events || events.length === 0) return [];
    type Row = {
      campaign_id: string;
      sent_at: string;
      channel: string | null;
      content_summary: string | null;
      outcome: "sent" | "responded" | "converted" | "ignored";
      outcome_at: string | null;
      outcome_type: string | null;
      decision_trace: any | null;
      outcome_event_id: string | null;
    };
    const map = new Map<string, Row>();
    for (const e of events) {
      const type = e.event_type as string;
      const payload = (e.payload ?? {}) as Record<string, unknown>;
      const cid = payload.campaign_id as string | undefined;
      if (!cid) continue;
      if (!["CampaignSent", "CampaignResponded", "CampaignConverted", "CampaignIgnored"].includes(type)) continue;
      let row = map.get(cid);
      if (!row) {
        row = {
          campaign_id: cid,
          sent_at: e.occurred_at,
          channel: null,
          content_summary: null,
          outcome: "sent",
          outcome_at: null,
          outcome_type: null,
          decision_trace: null,
          outcome_event_id: null,
        };
        map.set(cid, row);
      }
      if (type === "CampaignSent") {
        row.sent_at = e.occurred_at;
        row.channel = (payload.channel as string) ?? null;
        row.content_summary = (payload.content_summary as string) ?? null;
        row.decision_trace = (payload.decision_trace as any) ?? null;
      } else if (type === "CampaignConverted") {
        row.outcome = "converted";
        row.outcome_at = e.occurred_at;
        row.outcome_type = (payload.outcome_type as string) ?? null;
        row.outcome_event_id = (payload.outcome_event_id as string) ?? null;
      } else if (type === "CampaignResponded" && row.outcome !== "converted") {
        row.outcome = "responded";
        row.outcome_at = e.occurred_at;
      } else if (type === "CampaignIgnored" && row.outcome === "sent") {
        row.outcome = "ignored";
        row.outcome_at = e.occurred_at;
      }
    }
    return Array.from(map.values()).sort(
      (a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime(),
    );
  }, [events]);

  const selectedCampaign = useMemo(
    () => campaignHistory.find((c) => c.campaign_id === selectedCampaignId) ?? null,
    [campaignHistory, selectedCampaignId],
  );

  // For Converted campaigns, resolve the attributed booking.
  // outcome_event_id points to a BookingCreated/PaymentSucceeded event whose
  // payload carries booking_id; we then look up the booking row for amount + label.
  const { data: attributedBooking } = useQuery({
    queryKey: ["attributed_booking", selectedCampaign?.outcome_event_id],
    enabled: !!selectedCampaign?.outcome_event_id && selectedCampaign.outcome === "converted",
    queryFn: async () => {
      const eventId = selectedCampaign!.outcome_event_id!;
      const { data: evt } = await (supabase as any)
        .from("contact_events")
        .select("event_type, payload")
        .eq("event_id", eventId)
        .maybeSingle();
      const bookingId = (evt?.payload as Record<string, unknown> | null)?.booking_id as string | undefined;
      if (!bookingId) return null;
      const { data: booking } = await (supabase as any)
        .from("bookings")
        .select("booking_id, short_id, created_at, status, listings(name, price)")
        .eq("booking_id", bookingId)
        .maybeSingle();
      return booking;
    },
  });

  const handleSendWinback = async (includeImages?: boolean) => {
    if (!contactId || !customer?.business_id) {
      toast.error("Missing contact or business reference");
      return;
    }
    setIsSendingWinback(true);
    try {
      const { data, error } = await (supabase.functions as any).invoke("send-winback-now", {
        body: {
          contact_id: contactId,
          business_id: customer.business_id,
          // Only send an explicit override when the owner picked one; omitted
          // lets the backend fall back to the business-wide default.
          ...(typeof includeImages === "boolean" ? { include_images: includeImages } : {}),
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("Winback message sent");
      // Give the outbox drain a moment, then refresh events.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["customer_events", contactId] });
      }, 1500);
    } catch (err: any) {
      console.error("send-winback-now failed:", err);
      toast.error(err.message || "Failed to send winback");
    } finally {
      setIsSendingWinback(false);
    }
  };

  const activityData = useMemo(() => {
    if (!events || events.length === 0) return [];

    const now = new Date();
    let startDate: Date;

    if (timeRange === '1w') startDate = subDays(now, 7);
    else if (timeRange === '1m') startDate = subMonths(now, 1);
    else {
      const eventDates = events.map(e => new Date(e.occurred_at).getTime());
      startDate = new Date(Math.min(...eventDates));
    }

    const buckets = new Map<string, any>();
    const current = new Date(startDate);
    while (current <= now) {
      const key = format(current, "MMM d");
      buckets.set(key, {
        date: key,
        count: 0,
        messages: 0,
        bookings: 0,
        fullDate: format(current, "MMMM d, yyyy")
      });
      current.setDate(current.getDate() + 1);
    }

    for (const e of events) {
      const ts = new Date(e.occurred_at);
      if (!isAfter(ts, startDate)) continue;
      const key = format(ts, "MMM d");
      const bucket = buckets.get(key);
      if (bucket) {
        bucket.count++;
        const type = (e.event_type || "").toLowerCase();
        if (type.includes("message")) bucket.messages++;
        if (type.includes("booking")) bucket.bookings++;
      }
    }

    return Array.from(buckets.values());
  }, [events, timeRange]);

  const recentTimeline = useMemo(() => {
    // Dev-only samples so the layout can be vetted; never shown in production or alongside real runs.
    const daysAgo = (days: number) => new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    const sampleRuns = import.meta.env.DEV && workflowRuns && workflowRuns.length === 0
      ? [
          { id: "sample-1", action: "activate_workflow", created_at: daysAgo(3), selected_workflow_id: "idle-lead-winback", reason: "New enquiry went quiet for 3 days with no reply, so a single follow-up was started." },
          { id: "sample-2", action: "escalate_to_human", created_at: daysAgo(12), selected_workflow_id: "new-lead-qualification", reason: "Asked to speak to a person, so the AI handed the chat to staff." },
          { id: "sample-3", action: "activate_workflow", created_at: daysAgo(31), selected_workflow_id: "new-lead-qualification", reason: "New enquiry asking about trial classes. No active conversation, so this workflow started." },
        ]
      : [];
    const runEvents = [...(workflowRuns ?? []), ...sampleRuns].map((run) => ({
      event_id: `workflow-${run.id}`,
      event_type: run.action,
      occurred_at: run.created_at,
      workflow: { name: workflowDisplayName(run.selected_workflow_id) ?? "Workflow", reason: run.reason },
    }));
    return [...(events ?? []), ...runEvents]
      .sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at))
      .slice(0, 20);
  }, [events, workflowRuns]);

  const recentActivityByMonth = useMemo(() => {
    const groups = new Map<string, { label: string; events: any[] }>();
    for (const event of recentTimeline) {
      const occurredAt = new Date(event.occurred_at);
      const hasValidDate = !Number.isNaN(occurredAt.getTime());
      const key = hasValidDate ? format(occurredAt, "yyyy-MM") : "unknown";
      const label = hasValidDate ? format(occurredAt, "MMMM yyyy") : "Unknown date";
      const group = groups.get(key) ?? { label, events: [] };
      group.events.push(event);
      groups.set(key, group);
    }
    return Array.from(groups.entries()).map(([key, group]) => ({ key, ...group }));
  }, [recentTimeline]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card border border-border rounded-lg shadow-xl p-3 text-[11px] min-w-[140px]">
          <p className="font-bold text-foreground mb-2 border-b pb-1">{data.fullDate}</p>
          <div className="space-y-1.5">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Total activity</span>
              <span className="font-semibold text-foreground">{data.count}</span>
            </div>
            {data.messages > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Messages</span>
                <span className="font-semibold text-indigo-600">{data.messages}</span>
              </div>
            )}
            {data.bookings > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Bookings</span>
                <span className="font-semibold text-emerald-600">{data.bookings}</span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  // Pending actions awaiting approval for this contact. Drives both the tab
  // badge here and the queue rendered inside the "Actions queue" tab.
  const { items: approvalItems } = useContactApprovalQueue({
    businessId: customer?.business_id ?? "",
    contactId,
    email: customer?.email,
    phone: customer?.phone,
    name: customer?.name,
  });
  const approvalCount = approvalItems.length;
  const relationshipSuggestionCount = useRelationshipSuggestionCount(customer?.business_id, customerId);

  if (!profileReference) {
    return (
      <div className="max-w-3xl mx-auto py-12 text-center">
        <p className="text-muted-foreground">No {customerTerm.toLowerCase()} selected.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/?tab=customers")}>
          Back to {customerTerms.toLowerCase()}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (customerLoadError) {
    return (
      <div className="mx-auto max-w-3xl py-12 text-center" role="alert">
        <AlertTriangle className="mx-auto h-8 w-8 text-amber-600" />
        <p className="mt-3 font-medium">{customerTerm} profile could not be loaded</p>
        <p className="mt-1 text-sm text-muted-foreground">Dibs could not verify this {customerTerm.toLowerCase()} record. Try again before relying on the profile.</p>
        <Button variant="outline" className="mt-4" onClick={() => refetchCustomer()}>
          Try again
        </Button>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="max-w-3xl mx-auto py-12 text-center">
        <p className="text-muted-foreground">{customerTerm} not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/?tab=customers")}>
          Back to {customerTerms.toLowerCase()}
        </Button>
      </div>
    );
  }

  // One lifecycle source of truth: the time-aged Customer Graph projection.
  // GeneralCustomers uses this same field, so the board and profile cannot
  // disagree because of separate browser-time calculations.
  const rawStatus = String(projection?.status ?? "");
  const lifecycleStage: PipelineStage | null = rawStatus === "at_risk"
    ? "at-risk"
    : (["new", "active", "dormant", "lapsed", "vip"] as string[]).includes(rawStatus)
    ? rawStatus as PipelineStage
    : null;
  const status = lifecycleStage ? STAGE_META[lifecycleStage].label : "—";
  const statusColor =
    lifecycleStage === "active"
      ? "text-emerald-600"
      : lifecycleStage === "vip"
        ? "text-violet-600"
        : lifecycleStage === "new"
          ? "text-sky-600"
          : lifecycleStage === "lapsed"
            ? "text-rose-600"
            : lifecycleStage
              ? "text-amber-600"
              : status.toLowerCase() === "active"
                ? "text-emerald-600"
                : "text-rose-600";
  const profileDataError = contactIdError || projectionError || totalOrderValueError;
  const engagement = !projectionError && projection?.engagement_score != null ? Number(projection.engagement_score).toFixed(1) : "—";
  const ltv = !projectionError && projection?.lifetime_value != null ? `$${Number(projection.lifetime_value).toFixed(2)}` : "—";
  const totalBookings = !projectionError && projection?.total_bookings != null ? projection.total_bookings : "—";
  const messages = !projectionError && projection?.total_messages != null ? projection.total_messages : "—";
  const preferred = projection?.preferred_channel ?? "—";
  const orderValue = totalOrderValueError || totalOrderValue == null
    ? "—"
    : `$${Number(totalOrderValue).toFixed(2)}`;
  // Never present stale inferred prose as current. Deterministic projection
  // text remains the instant fallback while the cold-path synopsis catches up.
  const llmGeneratedAt: string | null = projection?.synopsis_llm_generated_at ?? null;
  const lastProjectedAt: string | null = projection?.last_projected_at ?? null;
  const synopsisSchemaVersion = Number(projection?.synopsis_schema_version ?? 0);
  const synopsisGenerationStatus = String(projection?.synopsis_generation_status ?? "missing");
  const llmGeneratedMs = llmGeneratedAt ? new Date(llmGeneratedAt).getTime() : Number.NaN;
  const llmFresh = !!projection?.synopsis_llm && !!llmGeneratedAt &&
    synopsisSchemaVersion >= CURRENT_SYNOPSIS_SCHEMA_VERSION &&
    synopsisGenerationStatus === "ready" &&
    Number.isFinite(llmGeneratedMs) && Date.now() - llmGeneratedMs <= 14 * 86_400_000 &&
    (!lastProjectedAt || llmGeneratedMs >= new Date(lastProjectedAt).getTime());
  const synopsis: string | null = llmFresh
    ? projection.synopsis_llm
    : projection?.synopsis ?? null;
  const synopsisAt: string | null = llmFresh
    ? llmGeneratedAt
    : projection?.synopsis_generated_at ?? null;
  const rawIntel = llmFresh && projection?.synopsis_llm_data &&
      typeof projection.synopsis_llm_data === "object"
    ? projection.synopsis_llm_data as Record<string, any>
    : {};
  const intelList = (key: string, max: number) =>
    Array.isArray(rawIntel[key])
      ? rawIntel[key].filter((item: unknown): item is string => typeof item === "string")
        .map((item: string) => item.trim()).filter(Boolean).slice(0, max)
      : [];
  const preferences = intelList("preferences", 4);
  const conversationHooks = intelList("conversation_hooks", 3);
  const likelyObjections = intelList("likely_objections", 3);
  const doNotMention = intelList("do_not_mention", 6);
  const evidence = Array.isArray(rawIntel.evidence) ? rawIntel.evidence.slice(0, 12) : [];
  const recommendedNextStep = [rawIntel.reengagement_angle, rawIntel.predicted_next]
    .find((value): value is string => typeof value === "string" && value.trim().length > 0)
    ?.trim() ?? null;
  const profileConfidence = ["low", "medium", "high"].includes(rawIntel.confidence)
    ? rawIntel.confidence
    : evidence.length >= 3 ? "medium" : "low";
  const aiBriefState = llmFresh
    ? "inferred"
    : synopsisGenerationStatus === "queued" || synopsisGenerationStatus === "generating"
      ? "refreshing"
      : synopsisGenerationStatus === "failed"
        ? "failed"
        : projection?.synopsis_llm
          ? "outdated"
          : "missing";

  // Rich-profile fields (computed by the rich_profile projection, never LLM-written).
  const spendTrend: string = projection?.spend_trend ?? "none";
  const cadenceDays: number | null = projection?.visit_cadence_days != null ? Number(projection.visit_cadence_days) : null;
  const activeHours: { band: string; weekend_lean: string } | null = projection?.active_hours ?? null;
  const riskFlags: string[] = projection?.risk_flags ?? [];
  const visibleRiskFlags = riskFlags.filter((flag) => flag !== "no_show_history");
  const trendLabel = spendTrend === "none" ? "—" : spendTrend === "growing" ? "↑ Growing" : spendTrend === "declining" ? "↓ Declining" : "→ Steady";
  const riskLabels: Record<string, string> = {
    dormant_overdue: "Overdue for a visit",
    payment_failed: "Recent payment failure",
    ignored_last_campaign: "Ignored last campaign",
  };
  const insights = customerInsightsFromProjection(projectionError ? null : projection);
  const renderAttributeValue = (attribute: CustomerAttributeDefinition) => {
    switch (attribute.key) {
      case "status":
        return <span className={cn("capitalize", statusColor)}>{status}</span>;
      case "email":
        return customer.email || "—";
      case "phone":
        return customer.phone || "—";
      case "bookings":
        return totalBookings;
      case "orderValue":
        return ltv === "—" ? orderValue : ltv;
      case "joined":
        return safeFormat(customer.created_at, "MMM d, yyyy");
      case "activity":
        return safeFormat(projection?.last_active_at, "MMM d, yyyy");
      case "interest":
      case "bestTime":
      case "nextStep":
        return insights[attribute.key] ?? "—";
      case "sentiment":
        return insights.sentiment ? SENTIMENT_META[insights.sentiment].label : "—";
      default: {
        const value = contactCustomAttributes[attribute.key.replace(/^custom:/, "")];
        if (value == null || value === "") return "—";
        return typeof value === "object" ? JSON.stringify(value) : String(value);
      }
    }
  };
  const isEditableAttribute = (attribute: CustomerAttributeDefinition) =>
    attribute.key === "email"
    || attribute.key === "phone"
    || (attribute.source === "given" && attribute.key.startsWith("custom:"));
  const editableAttributeValue = (attribute: CustomerAttributeDefinition) => {
    if (attribute.key === "email") return customer.email ?? "";
    if (attribute.key === "phone") return customer.phone ?? "";
    const value = contactCustomAttributes[attribute.key.replace(/^custom:/, "")];
    if (value == null) return "";
    return typeof value === "object" ? JSON.stringify(value) : String(value);
  };
  const beginAttributeEdit = (attribute: CustomerAttributeDefinition) => {
    if (!isEditableAttribute(attribute)) return;
    attributeEditCancelledRef.current = false;
    setEditingAttributeKey(attribute.key);
    setAttributeDraft(editableAttributeValue(attribute));
  };
  const saveAttributeValue = async (attribute: CustomerAttributeDefinition, value: string) => {
    const trimmed = value.trim();
    if (trimmed === editableAttributeValue(attribute)) return;

    if (attribute.key === "email" || attribute.key === "phone") {
      const field = attribute.key;
      // .select() so an update that RLS filtered down to zero rows is reported
      // as a failure rather than a silent "success".
      const { data: updatedRows, error } = customerId
        ? await supabase
            .from("customers")
            .update(field === "email" ? { email: trimmed || null } : { phone: trimmed || null })
            .eq("customer_id", customerId)
            .select("customer_id")
        : await supabase
            .from("contacts")
            .update(field === "email" ? { primary_email: trimmed || null } : { primary_phone: trimmed || null })
            .eq("contact_id", linkedContactId!)
            .select("contact_id");
      if (error || !updatedRows?.length) {
        toast.error(`Failed to update ${attribute.label.toLowerCase()}: ${error?.message ?? "you don't have permission to edit this customer"}`);
        return;
      }
      queryClient.setQueryData<CustomerProfileRecord | null>(
        ["customer-profile", customerId, linkedContactId],
        (current) => current
          ? { ...current, [field]: field === "email" ? trimmed : trimmed || null } as CustomerProfileRecord
          : current,
      );
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.success(`${attribute.label} updated successfully`);
      return;
    }

    if (!contactId || !customer.business_id) {
      toast.error("This attribute cannot be saved until the customer profile is linked.");
      return;
    }
    // Sets or clears this one key server-side, so attributes written
    // meanwhile by the Tally sync or import backfill are never overwritten by
    // a stale copy of the whole object.
    const { data: savedAttributes, error } = await supabase.rpc("set_contact_custom_attribute", {
      p_contact_id: contactId,
      p_key: attribute.key.replace(/^custom:/, ""),
      p_value: trimmed,
    });
    if (error) {
      toast.error(`Failed to update ${attribute.label.toLowerCase()}: ${error.message}`);
      return;
    }
    const nextAttributes = (savedAttributes as Record<string, unknown> | null) ?? {};
    queryClient.setQueryData(["contact_custom_attributes", contactId], nextAttributes);
    queryClient.setQueryData<CustomerProfileRecord | null>(
      ["customer-profile", customerId, linkedContactId],
      (current) => current && current.custom_attributes ? { ...current, custom_attributes: nextAttributes } : current,
    );
    queryClient.invalidateQueries({ queryKey: ["customers"] });
    toast.success(`${attribute.label} updated successfully`);
  };
  const finishAttributeEdit = (attribute: CustomerAttributeDefinition) => {
    if (attributeEditCancelledRef.current) return;
    const nextValue = attributeDraft;
    setEditingAttributeKey(null);
    setAttributeDraft("");
    void saveAttributeValue(attribute, nextValue);
  };
  const contactIdentity = primaryIdentity({
    identifiers: channelData?.identifiers ?? [],
    preferredChannel: projection?.preferred_channel ?? null,
    primaryPhone: customer.phone,
    primaryEmail: customer.email,
  });
  const displayName = contactDisplayName(customer.name, contactIdentity);
  // Shown under the name: a phone, @handle or email, never a bare channel id.
  const identitySubtitle = contactIdentity && contactIdentity !== displayName && !isRawIdLabel(contactIdentity)
    ? contactIdentity
    : null;
  const customerInitial = displayName.replace(/^@/, "").trim().charAt(0).toUpperCase() || "U";

  return (
    <div>
      {profileDataError && (
        <div className="mb-4 flex flex-col items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between" role="alert">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div>
              <p className="font-medium text-amber-950">Profile intelligence is temporarily unavailable</p>
              <p className="mt-1 text-sm text-amber-900/80">Unverified profile metrics are shown as dashes instead of zero.</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void Promise.all([
              refetchContactId(),
              refetchProjection(),
              refetchTotalOrderValue(),
            ])}
          >
            Try again
          </Button>
        </div>
      )}
      <div id="customer-profile-content" className="grid w-full grid-cols-1 lg:grid-cols-[380px_minmax(0,1fr)] lg:items-start">
        {/* Left column */}
        <div className="space-y-3 border-b border-border pb-6 lg:border-b-0 lg:border-r lg:pb-0 lg:pr-6">
          {/* Profile details */}
          <div className="space-y-3 py-1">
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-3">
                  <ContactPhoto
                    businessId={customer.business_id}
                    customerId={customerId}
                    contactId={contactId ?? null}
                    initial={customerInitial}
                    name={displayName}
                  />
                  <div className="min-w-0">
                    <h2 className="truncate text-xl font-heading font-semibold">{displayName}</h2>
                    {identitySubtitle || customer.short_id ? (
                      <p className="mt-1 truncate text-xs text-muted-foreground tracking-wide">
                        {[identitySubtitle, customer.short_id]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="mt-3 flex w-full flex-nowrap items-center gap-1.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="admin-toolbar-button min-w-0 !flex-1 gap-2"
                    aria-label={`Open chats with ${displayName}`}
                    title={`Open chats with ${displayName}`}
                    onClick={() => {
                      const next = new URLSearchParams();
                      next.set("tab", "inbox");
                      if (customerId) next.set("customer", customerId);
                      if (contactId ?? linkedContactId) next.set("contact", (contactId ?? linkedContactId)!);
                      if (searchParams.get("subdomain") === "admin") next.set("subdomain", "admin");
                      navigate(`?${next.toString()}`);
                    }}
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    Chat with customer
                  </Button>
                  <Button
                    onClick={() => setIsDeleteDialogOpen(true)}
                    variant="outline"
                    size="sm"
                    disabled={isDeleting}
                    className="admin-toolbar-button min-w-0 !flex-1 gap-2"
                  >
                    {isDeleting
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Trash2 className="h-3.5 w-3.5" />}
                    Delete customer
                  </Button>
                </div>
              </div>
              {visibleRiskFlags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {visibleRiskFlags.map((flag) => (
                    <Badge key={flag} variant="outline" className="bg-rose-500/10 text-rose-700 border-rose-500/30 text-[10px]">
                      {riskLabels[flag] ?? flag}
                    </Badge>
                  ))}
                </div>
              )}

              <PlanFeatureGate
                feature="synopsis_generation"
                featureName="AI customer insights"
                variant="section"
                className="min-h-0"
              >
                <div className="divide-y divide-border">
                  <section className="pb-3">
                    <div className="mb-1.5 flex items-center gap-2 mt-4">
                      <h3 className="text-sm font-heading font-semibold text-foreground">Synopsis</h3>
                      <Sparkles className="h-4 w-4 text-primary" aria-label="AI generated" />
                    </div>
                    {synopsis ? (
                      <p className="text-sm leading-relaxed text-foreground">{naturalizeSynopsis(synopsis)}</p>
                    ) : (
                      <p className="text-sm text-foreground">Synopsis not yet generated.</p>
                    )}
                    {synopsisAt && (
                      <p className="mt-1.5 text-[11px] text-muted-foreground">Generated {safeDistance(synopsisAt)}</p>
                    )}
                  </section>

                  <section className="py-3">
                    <div className="mb-1.5 flex items-center gap-2">
                      <h3 className="text-sm font-heading font-semibold text-foreground">Recommended next step</h3>
                      <Sparkles className="h-4 w-4 text-primary" aria-label="AI generated" />
                    </div>
                    {recommendedNextStep ? (
                      <p className="text-sm leading-relaxed text-foreground">{recommendedNextStep}</p>
                    ) : (
                      <p className="text-sm text-foreground">No recommended next step yet.</p>
                    )}
                  </section>
                </div>
              </PlanFeatureGate>

              <div className="space-y-2 border-t border-border pt-4">
                <h3 className="text-sm font-heading font-semibold text-foreground">Attributes</h3>
                <div className="space-y-1">
                  {customerAttributeDefinitions.map((attribute) => {
                    const Icon = attribute.icon;
                    const value = renderAttributeValue(attribute);
                    const editable = isEditableAttribute(attribute);
                    const valueTitle = typeof value === "string" || typeof value === "number"
                      ? String(value)
                      : undefined;
                    return (
                      <div
                        key={attribute.key}
                        className="grid grid-cols-[150px_minmax(0,1fr)] items-center gap-3 text-sm"
                      >
                        <span className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
                          <Icon className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{attribute.label}</span>
                        </span>
                        {editingAttributeKey === attribute.key ? (
                          <Input
                            autoFocus
                            type={attribute.key === "email" ? "email" : "text"}
                            value={attributeDraft}
                            aria-label={`Edit ${attribute.label}`}
                            className="h-7 min-w-0 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                            onChange={(event) => setAttributeDraft(event.target.value)}
                            onBlur={() => finishAttributeEdit(attribute)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") event.currentTarget.blur();
                              if (event.key === "Escape") {
                                attributeEditCancelledRef.current = true;
                                setEditingAttributeKey(null);
                                setAttributeDraft("");
                              }
                            }}
                          />
                        ) : (
                          <span
                            className={cn(
                              "min-w-0 truncate py-1 text-foreground",
                              editable && "-mx-2 cursor-text rounded-md px-2 hover:bg-muted/40",
                            )}
                            title={valueTitle}
                            role={editable ? "button" : undefined}
                            tabIndex={editable ? 0 : undefined}
                            aria-label={editable ? `Edit ${attribute.label}` : undefined}
                            onClick={() => beginAttributeEdit(attribute)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                beginAttributeEdit(attribute);
                              }
                            }}
                          >
                            {value}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <ContactChannelsSection
                contactId={contactId ?? null}
                identifiers={channelData?.identifiers ?? []}
                socialHandles={channelData?.socialHandles ?? {}}
                preferredChannel={projection?.preferred_channel ?? null}
                onSocialHandlesChange={() => void refetchChannels()}
              />

              <div className="space-y-2 border-t border-border pt-3">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-heading font-semibold text-foreground">Groups</h3>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs font-normal text-muted-foreground hover:bg-muted hover:text-foreground"
                        disabled={contactGroups.length === 0}
                      >
                        Manage its groups
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      {contactGroups.map((group) => (
                        <DropdownMenuCheckboxItem
                          key={group.id}
                          checked={memberGroupIds.has(group.id)}
                          disabled={pendingGroupId === group.id}
                          onSelect={(event) => event.preventDefault()}
                          onCheckedChange={(checked) => updateGroupMembership(group, checked)}
                        >
                          {group.name}
                          {pendingGroupId === group.id && <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin" />}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {memberGroups.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {memberGroups.map((group) => (
                      <Badge
                        key={group.id}
                        variant="outline"
                        className="rounded-md border-border bg-muted font-medium text-foreground"
                      >
                        {group.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Not in any groups.</p>
                )}

                {contactGroups.length === 0 && (
                  <p className="text-xs text-muted-foreground">Create groups from the {customerTerms} tab first.</p>
                )}
              </div>
          </div>

          <div className={SHOW_LEFT_PROFILE_INTELLIGENCE ? "contents" : "hidden"}>
          {/* AI Insights */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">AI Insights</p>
            <Card className="relative overflow-hidden bg-primary/5 border-primary/20">
              <CardContent className="py-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                    Sales brief
                  </p>
                  {llmFresh ? (
                    <Badge variant="outline" className="text-[10px] capitalize">
                      Inferred · {profileConfidence} confidence · {evidence.length} sources
                    </Badge>
                  ) : synopsis ? (
                    <Badge variant="outline" className="text-[10px]">Calculated fallback</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] capitalize">{aiBriefState}</Badge>
                  )}
                </div>
                {synopsis ? llmFresh && contactId && customer?.business_id ? (
                  <InferredClaim
                    businessId={customer.business_id}
                    contactId={contactId}
                    claimKey="narrative"
                    claimText={synopsis}
                    className="text-sm text-foreground"
                  />
                ) : (
                  <p className="text-sm text-foreground">{synopsis}</p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">Synopsis not yet generated.</p>
                )}
                {synopsisAt && (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Generated {safeDistance(synopsisAt)}
                  </p>
                )}
                {!llmFresh && aiBriefState !== "missing" && (
                  <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    {aiBriefState === "failed"
                      ? "The inferred sales brief could not be refreshed. Showing calculated profile facts; no stale inference is being used."
                      : aiBriefState === "outdated"
                        ? "The inferred sales brief is outdated. Showing calculated profile facts until a grounded replacement is ready."
                        : "The inferred sales brief is being refreshed. Showing calculated profile facts for now."}
                  </div>
                )}

                {llmFresh && (
                  <div className="mt-4 space-y-3 border-t border-primary/10 pt-3">
                    {(rawIntel.predicted_next || rawIntel.reengagement_angle) && (
                      <div className="grid gap-2 sm:grid-cols-2">
                        {rawIntel.predicted_next && (
                          <div className="rounded-md border bg-background/70 p-2.5">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Likely next need</p>
                            <div className="mt-1">
                              <InferredClaim
                                businessId={customer.business_id}
                                contactId={contactId!}
                                claimKey="predicted_next"
                                claimText={String(rawIntel.predicted_next)}
                              />
                            </div>
                          </div>
                        )}
                        {rawIntel.reengagement_angle && (
                          <div className="rounded-md border bg-background/70 p-2.5">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Best sales angle</p>
                            <div className="mt-1">
                              <InferredClaim
                                businessId={customer.business_id}
                                contactId={contactId!}
                                claimKey="reengagement_angle"
                                claimText={String(rawIntel.reengagement_angle)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {rawIntel.suggested_offer && (
                      <div className="rounded-md border bg-background/70 p-2.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Suggested offer angle</p>
                        <div className="mt-1">
                          <InferredClaim
                            businessId={customer.business_id}
                            contactId={contactId!}
                            claimKey="suggested_offer"
                            claimText={String(rawIntel.suggested_offer)}
                          />
                        </div>
                      </div>
                    )}
                    {preferences.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Grounded preferences</p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {preferences.map((item) => (
                            <span key={item} className="inline-flex items-center rounded-md bg-secondary pr-0.5">
                              <Badge variant="secondary" className="border-0 text-[10px]">{item}</Badge>
                              <ClaimFeedbackMenu
                                businessId={customer.business_id}
                                contactId={contactId!}
                                claimKey="preferences"
                                claimText={item}
                              />
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {conversationHooks.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Conversation openings</p>
                        <ul className="mt-1 space-y-1 text-xs text-foreground">
                          {conversationHooks.map((item) => (
                            <li key={item}>
                              <InferredClaim
                                businessId={customer.business_id}
                                contactId={contactId!}
                                claimKey="conversation_hooks"
                                claimText={item}
                                className="text-xs text-foreground before:mr-1 before:content-['•']"
                              />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {likelyObjections.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Friction to quietly reduce</p>
                        <ul className="mt-1 space-y-1 text-xs text-foreground">
                          {likelyObjections.map((item) => (
                            <li key={item}>
                              <InferredClaim
                                businessId={customer.business_id}
                                contactId={contactId!}
                                claimKey="likely_objections"
                                claimText={item}
                                className="text-xs text-foreground before:mr-1 before:content-['•']"
                              />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {doNotMention.length > 0 && (
                      <div className="rounded-md border border-rose-200 bg-rose-50 p-2.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-rose-700">Internal only — never say directly</p>
                        <p className="mt-1 text-xs text-rose-800">{doNotMention.join("; ")}</p>
                      </div>
                    )}
                    {evidence.length > 0 && (
                      <details className="rounded-md border bg-background/70 p-2.5">
                        <summary className="cursor-pointer text-xs font-medium">How the AI knows this ({evidence.length})</summary>
                        <div className="mt-2 space-y-2">
                          {evidence.map((item: any, index: number) => (
                            <div key={`${item.source_ref}-${index}`} className="text-xs">
                              <p>{String(item.claim ?? "Evidence")}</p>
                              <p className="text-[10px] text-muted-foreground">
                                {String(item.source_ref ?? "unknown source").startsWith("projection:") || String(item.source_ref ?? "").startsWith("business:")
                                  ? "Calculated"
                                  : "Verified"} · {String(item.source_ref ?? "unknown source").replace(/^(event|note|social):/, "$1 · ")} · {String(item.confidence ?? "low")} confidence
                              </p>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Stats grid */}
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground">Profile facts</p>
            <Badge variant="outline" className="text-[10px]">Calculated from activity</Badge>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <StatBlock
              label="Status"
              tooltip="Lifecycle label from how recently the contact was active: active (<30d), at risk (30–60d), dormant (60–180d), or lapsed (≥180d). VIP if active and a top-20% spender for this business; new if there's no activity yet."
              value={
                <span className={cn("capitalize", statusColor)}>
                  {status}
                </span>
              }
            />
            <StatBlock
              label="Engagement"
              tooltip="A 0–100 score: recency (up to 40) + inbound messages in the last 90 days (3 each, max 30) + bookings in the last 180 days (6 each, max 30)."
              value={engagement}
            />
            <StatBlock
              label="Order Value"
              tooltip="Total list price of every booking made by this contact (matched on email or phone), excluding cancelled bookings. This is what they booked, regardless of whether it was paid."
              value={orderValue}
            />
            <StatBlock
              label="LTV (Paid)"
              tooltip="Money actually collected: total succeeded payments minus refunds. Unpaid or cancelled bookings don't count."
              value={ltv}
            />
            <StatBlock
              label="Total Bookings"
              tooltip="Number of bookings this contact has completed, all-time."
              value={totalBookings}
            />
            <StatBlock
              label="Messages"
              tooltip="Number of inbound messages this contact has sent you, all-time. Replies you send back aren't counted."
              value={messages}
            />
            <StatBlock
              label="Preferred Channel"
              tooltip="The channel the contact used most over the last 30 days (WhatsApp, Instagram, email, or Telegram). Used to decide where to send outbound messages."
              value={<span className="capitalize text-base">{preferred}</span>}
            />
            <StatBlock label="Spend Trend" value={<span className="text-base">{trendLabel}</span>} />
            <StatBlock
              label="Visit Cadence"
              value={
                <span className="text-base">
                  {cadenceDays != null ? `~${cadenceDays}d` : "—"}
                </span>
              }
            />
            <StatBlock
              label="Usually Engages"
              value={
                <span className="capitalize text-base">
                  {activeHours ? `${activeHours.band}s${activeHours.weekend_lean !== "mixed" ? ` · ${activeHours.weekend_lean}s` : ""}` : "—"}
                </span>
              }
            />
          </div>
          </div>

        </div>

        {/* Right column */}
        <div className="space-y-4 pt-6 lg:pl-6 lg:pt-0">
          {/* Campaign history */}
          {SHOW_CAMPAIGN_HISTORY && (
          <Card className="shadow-none border-border">
            <CardHeader className="py-4 pb-4 flex flex-row items-center justify-between space-y-0">
              <div className="space-y-1">
                <CardTitle className="text-base font-heading font-semibold">Campaign history</CardTitle>
                <CardDescription className="text-sm">
                  Winback campaigns sent and their outcomes
                </CardDescription>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={isSendingWinback || !contactId}
                  >
                    {isSendingWinback ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <Send />
                    )}
                    Send winback
                    <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => handleSendWinback(true)}>
                    <ImageIcon className="h-4 w-4 mr-2" />
                    Send with image
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSendWinback(false)}>
                    <ImageOff className="h-4 w-4 mr-2" />
                    Send text-only
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </CardHeader>
            <CardContent>
              {campaignHistory.length === 0 ? (
                <div className="py-10 flex flex-col items-center justify-center text-center">
                  <div className="rounded-full bg-muted border border-border p-3 mb-2">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground font-medium">No campaigns sent to this {customerTerm.toLowerCase()} yet.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1 -mr-1">
                  {campaignHistory.map((row) => {
                    const outcomeStyles =
                      row.outcome === "converted"
                        ? { bg: "bg-emerald-50", text: "text-emerald-700", icon: <CheckCircle2 className="h-3 w-3" />, label: row.outcome_type === "booking_created" ? "Converted · booking" : row.outcome_type === "payment_succeeded" ? "Converted · payment" : "Converted" }
                        : row.outcome === "responded"
                          ? { bg: "bg-blue-50", text: "text-blue-700", icon: <MessageSquare className="h-3 w-3" />, label: "Responded" }
                          : row.outcome === "ignored"
                            ? { bg: "bg-muted", text: "text-muted-foreground", icon: <XCircle className="h-3 w-3" />, label: "Ignored" }
                            : { bg: "bg-amber-50", text: "text-amber-700", icon: <Clock className="h-3 w-3" />, label: "Awaiting response" };
                    return (
                      <button
                        key={row.campaign_id}
                        type="button"
                        onClick={() => setSelectedCampaignId(row.campaign_id)}
                        className="w-full text-left border border-border rounded-lg p-3 hover:bg-muted hover:border-border transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-foreground">
                                {row.campaign_id.startsWith("manual_winback")
                                  ? "Manual winback"
                                  : row.campaign_id.startsWith("dormant_winback")
                                    ? "Dormant winback"
                                    : row.campaign_id}
                              </span>
                              {row.channel && (
                                <Badge variant="outline" className="capitalize">
                                  {row.channel}
                                </Badge>
                              )}
                            </div>
                            {row.content_summary && (
                              <p className="text-xs text-muted-foreground line-clamp-2">{row.content_summary}</p>
                            )}
                            <p className="text-[10px] text-muted-foreground mt-1">
                              Sent {safeDistance(row.sent_at)}
                              {row.outcome_at && row.outcome !== "sent" && (
                                <> · {row.outcome === "ignored" ? "marked ignored" : row.outcome === "responded" ? "responded" : "converted"} {safeDistance(row.outcome_at)}</>
                              )}
                            </p>
                          </div>
                          <Badge
                            variant="secondary"
                            className={cn(
                              "shrink-0 gap-1 border-transparent",
                              outcomeStyles.bg,
                              outcomeStyles.text,
                            )}
                          >
                            {outcomeStyles.icon}
                            {outcomeStyles.label}
                          </Badge>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
          )}

          <Tabs defaultValue="approvals" className="w-full" data-testid="customer-profile-tabs">
            <TabsList className="bg-transparent border-b border-border p-0 w-full justify-start rounded-none h-auto gap-6 mb-4 shrink-0">
              <TabsTrigger
                value="approvals"
                className="bg-transparent rounded-none border-b-2 border-transparent px-0 pb-3 pt-2 text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none font-semibold text-sm transition-all flex items-center gap-2"
              >
                <ShieldCheck className="h-4 w-4" />
                Actions queue
                {approvalCount > 0 && (
                  <span className="ml-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
                    {approvalCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="relationships"
                className="bg-transparent rounded-none border-b-2 border-transparent px-0 pb-3 pt-2 text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none font-semibold text-sm transition-all flex items-center gap-2"
              >
                <Network className="h-4 w-4" />
                Relationships
                {relationshipSuggestionCount > 0 && (
                  <span className="ml-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
                    {relationshipSuggestionCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="activities"
                className="bg-transparent rounded-none border-b-2 border-transparent px-0 pb-3 pt-2 text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none font-semibold text-sm transition-all flex items-center gap-2"
              >
                <Activity className="h-4 w-4" />
                Activities
              </TabsTrigger>
              <TabsTrigger
                value="intel"
                className="bg-transparent rounded-none border-b-2 border-transparent px-0 pb-3 pt-2 text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none font-semibold text-sm transition-all flex items-center gap-2"
              >
                <BookOpen className="h-4 w-4" />
                Knowledge
              </TabsTrigger>
            </TabsList>

            <TabsContent value="approvals" className="mt-0 border-0 p-0 outline-none">
              {/* Pending actions awaiting approval, scoped to this contact. */}
              {customer.business_id ? (
                <ContactApprovalQueue
                  businessId={customer.business_id}
                  contactId={contactId}
                  email={customer.email}
                  phone={customer.phone}
                  name={customer.name}
                />
              ) : null}
            </TabsContent>

            <TabsContent value="relationships" className="mt-0 border-0 p-0 outline-none">
              {customer.business_id && customerId ? (
                <CustomerRelationships
                  businessId={customer.business_id}
                  customerId={customerId}
                  customerName={displayName}
                  onOpenCustomer={(id) => {
                    const target = new URL(buildAdminUrl(window.location, window.location.pathname));
                    target.search = "";
                    if (/^(localhost|127\.0\.0\.1|\[?::1\]?)$/.test(window.location.hostname)) target.searchParams.set("subdomain", "admin");
                    target.searchParams.set("tab", "customer-details");
                    target.searchParams.set("id", id);
                    window.open(target.toString(), "_blank", "noopener,noreferrer");
                  }}
                />
              ) : (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                  Relationships are available once this contact is a customer.
                </div>
              )}
            </TabsContent>

            <TabsContent value="activities" className="mt-0 border-0 p-0 outline-none space-y-4">
          {/* Activity over time */}
          <Card className="shadow-none border-border">
            <CardHeader className="py-4 pb-4 flex flex-row items-center justify-between space-y-0">
              <div className="space-y-1">
                <CardTitle className="text-base font-heading font-semibold">Activity over time</CardTitle>
                <CardDescription className="text-sm">
                  Engagement events over the selected period
                </CardDescription>
              </div>
              <div className="flex items-center gap-1 border rounded-lg p-1">
                <CalendarDays className="w-3.5 h-3.5 text-muted-foreground ml-2" />
                {(['all', '1m', '1w'] as const).map((r) => (
                  <Button
                    key={r}
                    variant={timeRange === r ? "secondary" : "ghost"}
                    size="sm"
                    className="h-7 px-3 text-xs"
                    onClick={() => setTimeRange(r)}
                  >
                    {r === 'all' ? 'All' : r === '1m' ? '1 month' : '1 week'}
                  </Button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              {activityData.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground">
                  No activity yet.
                </div>
              ) : (
                <div className="h-48 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={activityData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="activityFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#ef4444" stopOpacity={0.15} />
                          <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#f4f4f5" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10, fill: '#71717a' }}
                        axisLine={false}
                        tickLine={false}
                        dy={10}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: '#71717a' }}
                        axisLine={false}
                        tickLine={false}
                        width={40}
                      />
                      <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ stroke: "#fecaca", strokeWidth: 1 }}
                      />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="#ef4444"
                        strokeWidth={2}
                        fill="url(#activityFill)"
                        animationDuration={1000}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent activity */}
          <Card className="shadow-none border-border">
            <CardHeader className="py-4 pb-4">
              <div className="space-y-1">
                <CardTitle className="text-base font-heading font-semibold">Recent activity</CardTitle>
                <CardDescription className="text-sm">
                  Latest events from across all channels
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {recentTimeline.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">No events yet.</p>
              ) : (
                <div className="space-y-6">
                  {recentActivityByMonth.map((month) => (
                    <section key={month.key} aria-labelledby={`activity-month-${month.key}`}>
                      <h4
                        id={`activity-month-${month.key}`}
                        className="mb-3 text-sm font-semibold text-foreground"
                      >
                        {month.label}
                      </h4>
                      <div className="relative pl-6">
                        <div className="absolute left-[6px] top-2 bottom-2 w-0.5 bg-border" />
                        <div className="space-y-3">
                          {month.events.map((e: any) => (
                            <div key={e.event_id ?? `${e.event_type}-${e.occurred_at}`} className="relative">
                              <div className="absolute -left-6 top-2 h-3.5 w-3.5 rounded-full border-2 border-primary bg-background" />
                              <div className="flex items-center justify-between gap-3 rounded-lg border bg-card p-3">
                                <div className="min-w-0">
                                  <div className="mb-1 flex items-center gap-2">
                                    {e.workflow ? <Workflow className="h-3.5 w-3.5" /> : eventIcon(e.event_type ?? "")}
                                    <p className="text-sm font-medium">
                                      {e.workflow ? `${decisionOutcome(e.event_type).label === "Staff takeover" ? "Staff takeover" : "Workflow started"} · ${e.workflow.name}` : humanizeEventType(e.event_type ?? "")}
                                    </p>
                                    {e.source_channel && <ChannelIcon channel={e.source_channel} />}
                                  </div>
                                  {e.workflow?.reason && <p className="mb-1 line-clamp-2 text-xs text-muted-foreground">{e.workflow.reason}</p>}
                                  <p className="text-xs text-muted-foreground">
                                    {safeFormat(e.occurred_at, "h:mm a, MMM d, yyyy")}
                                  </p>
                                </div>
                                <p className="shrink-0 text-xs text-muted-foreground">
                                  {safeDistance(e.occurred_at)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
            </TabsContent>

            <TabsContent value="intel" className="mt-0 border-0 p-0 outline-none space-y-4">
              {customerId && customer.business_id && (
                <ContactNotesCard
                  businessId={customer.business_id}
                  customerId={customerId}
                  contactId={contactId ?? null}
                />
              )}
              {SHOW_SOCIAL_PROFILES && customerId && customer.business_id && (
                <ContactSocialProfilesCard
                  businessId={customer.business_id}
                  customerId={customerId}
                  contactId={contactId ?? null}
                />
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Dialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          if (isDeleting) return;
          setIsDeleteDialogOpen(open);
          if (!open) setDeleteConfirmation("");
        }}
      >
        <DialogContent className="admin-modal-content">
          <DialogHeader className="admin-modal-header">
            <DialogTitle className="font-heading">Permanently delete this {customerTerm.toLowerCase()}?</DialogTitle>
            <DialogDescription>
              This deletes personal data, AI memory, notes, social intel, and conversations. Financial booking records are retained only in redacted form. This cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (deleteConfirmationMatches) void handleDeleteCustomerData();
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="delete-customer-confirmation">
                Type <span className="font-semibold">{customerNameForDeletion}</span> to confirm
              </Label>
              <Input
                id="delete-customer-confirmation"
                autoFocus
                autoComplete="off"
                value={deleteConfirmation}
                disabled={isDeleting}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                aria-invalid={deleteConfirmation.length > 0 && !deleteConfirmationMatches}
              />
              {deleteConfirmation.length > 0 && !deleteConfirmationMatches && (
                <p className="text-xs text-destructive" role="alert">
                  The name must match exactly, including capitalization and spacing.
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="standard-action-button admin-modal-secondary-button neutral-action-button"
                disabled={isDeleting}
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeleteConfirmation("");
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                size="sm"
                className="standard-action-button gap-1.5"
                disabled={!deleteConfirmationMatches || isDeleting}
              >
                {isDeleting && <Loader2 className="h-4 w-4 animate-spin" />}
                Permanently delete
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <CampaignDecisionModal
        open={!!selectedCampaignId}
        onOpenChange={(open) => !open && setSelectedCampaignId(null)}
        campaign={selectedCampaign}
        attributedBooking={attributedBooking}
        customerName={customer?.name ?? null}
      />
    </div>
  );
};

// "Why this customer?" modal

interface CampaignDecisionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: {
    campaign_id: string;
    sent_at: string;
    channel: string | null;
    content_summary: string | null;
    outcome: "sent" | "responded" | "converted" | "ignored";
    outcome_at: string | null;
    outcome_type: string | null;
    decision_trace: any | null;
  } | null;
  attributedBooking: any | null;
  customerName: string | null;
}

const CampaignDecisionModal = ({
  open,
  onOpenChange,
  campaign,
  attributedBooking,
  customerName,
}: CampaignDecisionModalProps) => {
  if (!campaign) return null;
  const trace = campaign.decision_trace ?? {};
  const signals = trace.signals ?? {};
  const channelDecision = trace.channel_decision ?? {};
  const messageVariant = trace.message_variant ?? {};
  const triggeredBy = trace.triggered_by ?? null;
  const isManual = trace.trigger_source === "manual_admin";

  const outcomeBadge =
    campaign.outcome === "converted"
      ? { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500", label: campaign.outcome_type === "booking_created" ? "Converted · booking" : campaign.outcome_type === "payment_succeeded" ? "Converted · payment" : "Converted" }
      : campaign.outcome === "responded"
        ? { bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500", label: "Responded" }
        : campaign.outcome === "ignored"
          ? { bg: "bg-muted", text: "text-muted-foreground", dot: "bg-muted-foreground", label: "Ignored" }
          : { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500", label: "Awaiting response" };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
        <DialogHeader className="pb-2 border-b border-border">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="text-base font-semibold">
                {isManual ? "Manual winback" : "Dormant winback"}
                {customerName && (
                  <span className="text-muted-foreground font-normal"> · {customerName}</span>
                )}
              </DialogTitle>
              <DialogDescription className="text-xs mt-1">
                Sent {safeDistance(campaign.sent_at)} · {safeFormat(campaign.sent_at, "MMM d, yyyy 'at' h:mm a")}
              </DialogDescription>
            </div>
            <div
              className={cn(
                "shrink-0 inline-flex items-center gap-1.5 text-[11px] font-medium rounded-full px-2.5 py-1",
                outcomeBadge.bg,
                outcomeBadge.text,
              )}
            >
              <span className={cn("h-1.5 w-1.5 rounded-full", outcomeBadge.dot)} />
              {outcomeBadge.label}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 pt-3">
          {/* Rationale */}
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              Rationale
            </h3>
            <div className="space-y-4">
              {/* Targeting */}
              <div>
                <p className="text-[11px] font-medium text-muted-foreground mb-1.5">Targeting</p>
                {trace.segment_predicate_summary ? (
                  <p className="text-sm text-muted-foreground mb-3">
                    Matched the <span className="font-medium text-foreground">{trace.segment_name}</span> segment —
                    <span className="text-muted-foreground"> {trace.segment_predicate_summary}.</span>
                  </p>
                ) : isManual ? (
                  <p className="text-sm text-muted-foreground mb-3">
                    Manually triggered{triggeredBy?.email ? <> by <span className="font-medium text-foreground">{triggeredBy.email}</span></> : null}.
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground italic mb-3">
                    No decision trace recorded for this campaign.
                  </p>
                )}

                {trace.signals && (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      <SignalChip
                        label="Last activity"
                        value={
                          signals.days_since_last_activity != null
                            ? `${signals.days_since_last_activity}d ago`
                            : signals.last_activity_at
                              ? safeDistance(signals.last_activity_at)
                              : "—"
                        }
                      />
                      <SignalChip
                        label="Last booking"
                        value={
                          signals.last_booking_at
                            ? `${safeFormat(signals.last_booking_at, "MMM d, yyyy")}${signals.last_booking_value != null ? ` · $${Number(signals.last_booking_value).toFixed(2)}` : ""}`
                            : "—"
                        }
                      />
                      <SignalChip label="Total bookings" value={signals.total_bookings ?? "—"} />
                      <SignalChip
                        label="LTV"
                        value={signals.ltv != null ? `$${Number(signals.ltv).toFixed(2)}` : "—"}
                      />
                      <SignalChip
                        label="Engagement"
                        value={signals.engagement_score != null ? `${signals.engagement_score}` : "—"}
                      />
                    </div>
                    {signals.synopsis && (
                      <div className="mt-2 rounded-md border border-border bg-muted/50 p-2.5">
                        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-1">
                          Synopsis at time of send
                        </p>
                        <p className="text-xs text-muted-foreground">{signals.synopsis}</p>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Channel */}
              {channelDecision.chosen && (
                <div className="border-t border-border/60 pt-4">
                  <p className="text-[11px] font-medium text-muted-foreground mb-1.5">Channel</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Badge variant="outline" className="capitalize">{channelDecision.chosen}</Badge>
                    <span className="text-muted-foreground">
                      {channelDecision.reason === "preferred_channel"
                        ? "matches customer's preferred channel"
                        : "most recent identifier for this contact"}
                    </span>
                  </div>
                  {channelDecision.alternatives_considered?.length > 1 && (
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Other channels available: {channelDecision.alternatives_considered.filter((c: string) => c !== channelDecision.chosen).join(", ")}
                    </p>
                  )}
                </div>
              )}

              {/* Message */}
              {(messageVariant.content || campaign.content_summary) && (
                <div className="border-t border-border/60 pt-4">
                  <p className="text-[11px] font-medium text-muted-foreground mb-1.5">Message</p>
                  <div className="rounded-md border border-border bg-muted/50 p-3">
                    <p className="text-sm text-muted-foreground italic">
                      "{messageVariant.content ?? campaign.content_summary}"
                    </p>
                    {messageVariant.template_id && (
                      <p className="text-[10px] text-muted-foreground mt-2">Template: {messageVariant.template_id}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Outcome timeline */}
          <section className="border-t border-border pt-5">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              Outcome
            </h3>
            <div className="text-sm">
              <OutcomeStep done label="Sent" at={campaign.sent_at} />
              <OutcomeStep
                done={campaign.outcome === "responded" || campaign.outcome === "converted"}
                label="Customer responded"
                at={campaign.outcome === "responded" || campaign.outcome === "converted" ? campaign.outcome_at : null}
                pending={campaign.outcome === "sent"}
              />
              <OutcomeStep
                done={campaign.outcome === "converted"}
                label={
                  campaign.outcome === "converted"
                    ? campaign.outcome_type === "booking_created"
                      ? "Booking created"
                      : campaign.outcome_type === "payment_succeeded"
                        ? "Payment received"
                        : "Converted"
                    : "Conversion"
                }
                at={campaign.outcome === "converted" ? campaign.outcome_at : null}
                pending={campaign.outcome === "responded"}
                last
              />
            </div>

            {campaign.outcome === "converted" && attributedBooking && (
              <div className="mt-3 rounded-md border border-emerald-100 bg-emerald-50/50 p-3">
                <p className="text-[10px] font-medium uppercase tracking-wide text-emerald-700 mb-1">
                  Attributed booking
                </p>
                <p className="text-sm text-foreground font-medium">
                  {attributedBooking.listings?.name ?? "Booking"} · ${Number(attributedBooking.listings?.price ?? 0).toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {attributedBooking.short_id ?? attributedBooking.booking_id} · {safeFormat(attributedBooking.created_at, "MMM d, yyyy")}
                </p>
              </div>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SignalChip = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex flex-col justify-between min-h-[3.25rem] rounded-md border border-border bg-card p-2.5">
    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-0.5">{label}</p>
    <p className="text-sm font-medium text-foreground">{value}</p>
  </div>
);

const OutcomeStep = ({
  done,
  pending,
  label,
  at,
  last,
}: {
  done: boolean;
  pending?: boolean;
  label: string;
  at: string | null | undefined;
  last?: boolean;
}) => (
  <div className="relative flex gap-3 pb-4 last:pb-0">
    {!last && (
      <span className="absolute left-[5px] top-3 bottom-0 w-px bg-border" aria-hidden />
    )}
    <div
      className={cn(
        "relative z-10 mt-1 h-2.5 w-2.5 rounded-full shrink-0 ring-2 ring-background",
        done ? "bg-emerald-500" : pending ? "bg-amber-400 animate-pulse" : "bg-secondary",
      )}
    />
    <div className="flex items-baseline gap-2 -mt-0.5">
      <span className={cn("text-sm", done ? "text-foreground" : "text-muted-foreground")}>{label}</span>
      {at && <span className="text-xs text-muted-foreground">· {safeDistance(at)}</span>}
    </div>
  </div>
);

const CustomerDetails = () => {
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get("id");
  if (isDemoSampleCustomer(customerId)) return <SampleCustomerProfile />;
  return <CustomerProfileTemplate />;
};

export default CustomerDetails;
